import { db, storage } from "../firebase";
import {
  collection,
  addDoc,
  getDocs,
  query,
  orderBy,
  serverTimestamp,
  deleteDoc,
  doc,
  writeBatch
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject
} from "firebase/storage";

const MAX_DIMENSION = 2400;
const THUMB_DIMENSION = 500;
const WEBP_QUALITY = 0.82;
const THUMB_QUALITY = 0.78;

const resizeAndConvert = (img, maxDimension, quality = WEBP_QUALITY) =>
  new Promise((resolve) => {
    let { width, height } = img;
    if (width > maxDimension || height > maxDimension) {
      if (width >= height) {
        height = Math.round((height / width) * maxDimension);
        width = maxDimension;
      } else {
        width = Math.round((width / height) * maxDimension);
        height = maxDimension;
      }
    }
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    canvas.getContext("2d").drawImage(img, 0, 0, width, height);
    canvas.toBlob(
      (blob) => resolve(blob),
      "image/webp",
      quality
    );
  });

export const convertToWebP = (file) =>
  new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = async () => {
      URL.revokeObjectURL(objectUrl);
      try {
        const [fullBlob, thumbBlob] = await Promise.all([
          resizeAndConvert(img, MAX_DIMENSION, WEBP_QUALITY),
          resizeAndConvert(img, THUMB_DIMENSION, THUMB_QUALITY),
        ]);
        if (!fullBlob || !thumbBlob) { resolve({ file, converted: false }); return; }
        // If WebP is larger than the original, skip conversion for the full image
        if (fullBlob.size >= file.size) { resolve({ file, converted: false }); return; }
        const fullFile = new File([fullBlob], file.name, { type: "image/webp" });
        const thumbFile = new File([thumbBlob], file.name, { type: "image/webp" });
        resolve({ file: fullFile, thumbFile, converted: true, originalSize: file.size });
      } catch {
        resolve({ file, converted: false });
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve({ file, converted: false });
    };

    img.src = objectUrl;
  });

/**
 * Fetches all photos from Firestore "photos" collection, ordered by createdAt desc.
 * @returns {Promise<Array>} Array of photo objects with { id, ...data }
 */
export const getAllPhotos = async () => {
  try {
    const photosRef = collection(db, "photos");
    const q = query(photosRef, orderBy("createdAt", "desc"));
    const querySnapshot = await getDocs(q);
    
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error("Error fetching photos:", error);
    throw error;
  }
};

/**
 * Uploads multiple files to Storage and creates Firestore documents.
 * @param {File[]} files - Array of File objects to upload
 * @param {string} baseTitle - Default title for photos
 * @param {string} guestName - Name of the uploader
 * @param {Object} perFileTitles - Optional map of filename -> specific title
 * @returns {Promise<void>}
 */
// preConverted: optional array of convertToWebP results (avoids re-converting)
export const uploadPhotos = async (files, baseTitle, guestName, perFileTitles = {}, onProgress, preConverted) => {
  try {
    const uploadPromises = files.map(async (file, index) => {
      const originalName = file.name;

      let optimizedFile, thumbFile, converted;
      if (preConverted?.[index]) {
        ({ file: optimizedFile, thumbFile, converted } = preConverted[index]);
      } else {
        onProgress?.({ index, phase: "optimizing" });
        ({ file: optimizedFile, thumbFile, converted } = await convertToWebP(file));
      }

      const uniqueId = Date.now() + Math.random().toString(36).substring(2);
      const fullRef = ref(storage, `wedding-photos/${uniqueId}.webp`);
      const thumbRef = ref(storage, `wedding-photos/thumbs/${uniqueId}.webp`);

      onProgress?.({ index, phase: "uploading" });
      const [fullSnapshot, thumbSnapshot] = await Promise.all([
        uploadBytes(fullRef, optimizedFile),
        converted ? uploadBytes(thumbRef, thumbFile) : Promise.resolve(null),
      ]);

      const [downloadURL, thumbURL] = await Promise.all([
        getDownloadURL(fullSnapshot.ref),
        thumbSnapshot ? getDownloadURL(thumbSnapshot.ref) : Promise.resolve(null),
      ]);

      const title = perFileTitles[originalName] || baseTitle;

      await addDoc(collection(db, "photos"), {
        url: downloadURL,
        ...(thumbURL && { thumbUrl: thumbURL }),
        title: title,
        guestName: guestName,
        createdAt: serverTimestamp(),
        storagePath: fullSnapshot.ref.fullPath,
        ...(thumbSnapshot && { thumbStoragePath: thumbSnapshot.ref.fullPath }),
      });
    });

    await Promise.all(uploadPromises);
  } catch (error) {
    console.error("Error uploading photos:", error);
    throw error;
  }
};

/**
 * Deletes a photo from Firestore and Storage.
 * @param {string} photoId - Firestore document ID
 * @param {string} storagePath - Storage path of the file
 */
export const deletePhoto = async (photoId, storagePath, thumbStoragePath) => {
  try {
    const deletions = [];
    if (storagePath) {
      deletions.push(deleteObject(ref(storage, storagePath)).catch(err => {
        console.warn("Failed to delete file from storage:", err);
      }));
    }
    if (thumbStoragePath) {
      deletions.push(deleteObject(ref(storage, thumbStoragePath)).catch(err => {
        console.warn("Failed to delete thumb from storage:", err);
      }));
    }
    await Promise.all(deletions);
    await deleteDoc(doc(db, "photos", photoId));
  } catch (error) {
    console.error("Error deleting photo:", error);
    throw error;
  }
};

/**
 * Deletes multiple photos from Firestore and Storage.
 * @param {Array<{id: string, storagePath: string}>} photosToDelete - Array of photo objects to delete
 */
export const deletePhotosBulk = async (photosToDelete) => {
  try {
    // 1. Delete all from Storage in parallel
    const storagePromises = photosToDelete.flatMap(photo => {
      const ops = [];
      if (photo.storagePath) {
        ops.push(deleteObject(ref(storage, photo.storagePath)).catch(err => {
          console.warn(`Failed to delete file ${photo.storagePath} from storage:`, err);
        }));
      }
      if (photo.thumbStoragePath) {
        ops.push(deleteObject(ref(storage, photo.thumbStoragePath)).catch(err => {
          console.warn(`Failed to delete thumb ${photo.thumbStoragePath} from storage:`, err);
        }));
      }
      return ops;
    });
    
    await Promise.all(storagePromises);

    // 2. Delete from Firestore using batched writes (max 500 per batch)
    // Simple implementation for < 500 photos. For more, chunk it.
    const batch = writeBatch(db);
    photosToDelete.forEach(photo => {
      const docRef = doc(db, "photos", photo.id);
      batch.delete(docRef);
    });

    await batch.commit();
  } catch (error) {
    console.error("Error deleting photos in bulk:", error);
    throw error;
  }
};
